import { Fragment, useCallback, useLayoutEffect, useMemo } from 'react';

import { FlexAligner, Typography } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { Loader } from '@/components/Loader.tsx';
import { Tabs } from '@/components/tabs/Tabs.tsx';
import { useSiteContext } from '@/providers/appCoreProvider/accessibleSitesProvider/siteProvider/SiteContext.ts';
import { useConfigStore } from '@/stores';
import { InventorySiteSection } from '@/types.ts';
import { responsive } from '@/utils';

import { buildVerticalSiteTabItems } from './buildVerticalSiteTabItems.tsx';
import {
  WidgetSiteTabbedFlexFill,
  WidgetSiteTabbedPanelScroll,
} from './inventorySiteSections.styles.ts';
import { resolvePreferredSiteSection } from './siteTabbedInventoryLayout/siteTabbedInventoryLayout.functions.ts';
import type { SiteTabbedInventoryLayoutProps } from './siteTabbedInventoryLayout/siteTabbedInventoryLayout.types.ts';
import { useInventorySiteSections } from './widgetConfigSitePanel/useInventorySiteSections.ts';

export const SiteTabbedInventoryLayout = ({
  source,
  groupBy,
  formatTabLabel,
  showItemCount,
  hideSiteTabs = false,
  emptyMessage,
  isOpen = true,
  renderPanel,
  selectedSiteKey,
  onSiteKeyChange,
  defaultInventoryId,
  defaultSiteKey,
  tabsDataAttributes,
  singleSitePanelDataAttributes,
  onPanelsReady,
  layout = 'shell',
  keepTabPanelsMounted = false,
}: SiteTabbedInventoryLayoutProps) => {
  const { t } = useTranslation();
  const { currentSite, sites } = useSiteContext();
  const portableMode = useConfigStore(s => s.portableMode);
  const { siteSections, isLoading, isEmpty } = useInventorySiteSections({ source, groupBy });

  const isShellLayout = layout === 'shell';
  const showSiteTabs = !hideSiteTabs && (portableMode || (sites?.length ?? 0) > 1);
  const showTabContent = isOpen && !isLoading && !isEmpty;
  const isControlledTabs = onSiteKeyChange !== undefined;

  const resolvedDefaultSiteKey = useMemo(() => {
    if (defaultSiteKey) return defaultSiteKey;
    if (defaultInventoryId) {
      return (
        siteSections.find(section => section.items.some(item => item.id === defaultInventoryId))
          ?.siteKey ?? siteSections[0]?.siteKey
      );
    }
    return siteSections[0]?.siteKey;
  }, [defaultInventoryId, defaultSiteKey, siteSections]);

  const singleSiteSection = useMemo(
    () => resolvePreferredSiteSection(siteSections, currentSite, sites),
    [currentSite, siteSections, sites]
  );

  useLayoutEffect(() => {
    if (!isControlledTabs || !showTabContent) return;

    if (showSiteTabs && resolvedDefaultSiteKey) {
      onSiteKeyChange(resolvedDefaultSiteKey);
      return;
    }

    if (singleSiteSection?.siteKey) {
      onSiteKeyChange(singleSiteSection.siteKey);
    }
  }, [
    isControlledTabs,
    onSiteKeyChange,
    resolvedDefaultSiteKey,
    showSiteTabs,
    showTabContent,
    singleSiteSection?.siteKey,
  ]);

  useLayoutEffect(() => {
    if (!showTabContent) return;
    onPanelsReady?.();
  }, [onPanelsReady, showTabContent]);

  const tabItems = useMemo(() => {
    if (!showSiteTabs) return [];

    return buildVerticalSiteTabItems({
      sections: siteSections,
      formatTabLabel,
      showItemCount,
      renderPanel,
      keepTabPanelsMounted,
    });
  }, [
    formatTabLabel,
    keepTabPanelsMounted,
    renderPanel,
    showItemCount,
    showSiteTabs,
    siteSections,
  ]);

  const renderSingleSitePanel = useCallback(
    (section: InventorySiteSection) => {
      const panel = renderPanel(section);
      if (!isShellLayout) return panel;

      return (
        <WidgetSiteTabbedPanelScroll {...singleSitePanelDataAttributes}>
          {panel}
        </WidgetSiteTabbedPanelScroll>
      );
    },
    [renderPanel, singleSitePanelDataAttributes, isShellLayout]
  );

  const panelContent =
    showTabContent &&
    (showSiteTabs ? (
      <Tabs
        orientation='vertical'
        enableFirstLetterTypeahead
        heading={t('deviceTypes.Site')}
        {...(isControlledTabs
          ? { value: selectedSiteKey ?? resolvedDefaultSiteKey, onValueChange: onSiteKeyChange }
          : { defaultValue: resolvedDefaultSiteKey })}
        items={tabItems}
      />
    ) : (
      singleSiteSection && renderSingleSitePanel(singleSiteSection)
    ));

  const Root = isShellLayout ? WidgetSiteTabbedFlexFill : Fragment;
  const PanelWrapper = isShellLayout ? WidgetSiteTabbedFlexFill : Fragment;
  const panelWrapperProps = isShellLayout ? tabsDataAttributes : undefined;

  return (
    <Root>
      <Loader render={isLoading} />
      {isEmpty && (
        <FlexAligner
          direction='column'
          gap={responsive.spacing.medium}
          style={{ padding: '1.25rem' }}
        >
          <Typography type='body_large' secondary>
            {emptyMessage ?? t('notAvailable')}
          </Typography>
        </FlexAligner>
      )}
      {panelContent && <PanelWrapper {...panelWrapperProps}>{panelContent}</PanelWrapper>}
    </Root>
  );
};
